/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { css, jsx } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema';
import { ProviderFactory, type ProviderHandler } from '@atlaskit/editor-common/provider-factory';
import { ReactRenderer } from '@atlaskit/renderer';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { N800 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

type PreviewRendererProps = {
	jsonDocument: DocNode;
	showTelepointer: boolean;
	providerFactory: ProviderFactory;
	reduceMargins?: boolean;
};

// override renderer's syntax highlighting styles so it appears consistent with the editor
const codeBlockStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.code-block .token.token': {
		fontStyle: 'normal',
		fontWeight: token('font.weight.regular'),
		color: token('color.text', N800),
	},
});

const reducedMarginStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.ak-renderer-document >:first-child': {
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'.pm-table-wrapper > table': {
			marginTop: 0,
		},
		marginTop: 0,
	},
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.ak-renderer-document >:last-child': {
		marginBottom: 0,
	},
});

export const PreviewRenderer = ({
	jsonDocument,
	showTelepointer,
	providerFactory,
	reduceMargins,
}: PreviewRendererProps) => {
	const previewRendererRef = useRef<HTMLDivElement>(null);
	// IMPORTANT: the renderer will call providerFactory.destroy() when it unmounts. This is why we need to duplicate
	// the providerFactory and add a heap of subscribers to it. This is to avoid this duplicate provider gettting out of
	// sync with the original providerFactory.
	const dupedProviderFactory = useRef(new ProviderFactory());
	useEffect(() => {
		const mentionHandler: ProviderHandler<'mentionProvider'> = (name, provider) => {
			dupedProviderFactory.current.setProvider(name, provider);
		};
		const emojiHandler: ProviderHandler<'emojiProvider'> = (name, provider) => {
			dupedProviderFactory.current.setProvider(name, provider);
		};
		const mediaHandler: ProviderHandler<'mediaProvider'> = (name, provider) => {
			dupedProviderFactory.current.setProvider(name, provider);
		};
		providerFactory.subscribe('mentionProvider', mentionHandler);
		providerFactory.subscribe('emojiProvider', emojiHandler);
		providerFactory.subscribe('mediaProvider', mediaHandler);

		return () => {
			providerFactory.unsubscribe('mentionProvider', mentionHandler);
			providerFactory.unsubscribe('emojiProvider', emojiHandler);
			providerFactory.unsubscribe('mediaProvider', mediaHandler);
		};
	}, [providerFactory]);

	return (
		<div
			data-testid="preview-renderer"
			css={[codeBlockStyles, reduceMargins && reducedMarginStyles]}
			ref={previewRendererRef}
		>
			<RendererActionsContext>
				<ReactRenderer
					document={jsonDocument}
					media={{
						// We don't currently support images with captions
						allowLinking: false,
					}}
					// By default -- the renderer uses a "default schema" which supports all
					// the nodes and marks the markdown transformer can create.
					// see packages/editor/adf-schema/src/schema/default-schema.ts for details.
					useSpecBasedValidator
					addTelepointer={showTelepointer}
					dataProviders={dupedProviderFactory.current}
					allowPlaceholderText
				/>
			</RendererActionsContext>
		</div>
	);
};
